import type { DeckMetafyArticle } from '../types/deck';

export const METAFY_ARTICLE_URL_MAX_CHARS = 500;
export const METAFY_ARTICLE_TITLE_MAX_CHARS = 160;

const GUIDE_PATH = /^\/guides\/view\/([A-Za-z0-9_-]+)\/?$/;

/** Canonical `https://metafy.gg/guides/view/{slug}` plus slug, or null. */
export function parseMetafyGuideUrl(input: string): { url: string; slug: string } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    if (host !== 'metafy.gg') return null;
    const match = u.pathname.match(GUIDE_PATH);
    if (!match) return null;
    return {
      url: `https://metafy.gg/guides/view/${match[1]}`,
      slug: match[1],
    };
  } catch {
    return null;
  }
}

export function sanitizeMetafyArticleUrl(raw: string): string {
  return raw.trim().slice(0, METAFY_ARTICLE_URL_MAX_CHARS);
}

export function metafyArticlesEqual(a?: DeckMetafyArticle, b?: DeckMetafyArticle): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.url === b.url &&
    (a.title ?? '') === (b.title ?? '') &&
    (a.coverUrl ?? '') === (b.coverUrl ?? '') &&
    (a.priceCents ?? null) === (b.priceCents ?? null) &&
    (a.currency ?? '') === (b.currency ?? '') &&
    Boolean(a.subscriberOnly) === Boolean(b.subscriberOnly)
  );
}

export function normalizeMetafyArticle(value: unknown): DeckMetafyArticle | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  if (typeof raw.url !== 'string' || !raw.url.trim()) return undefined;

  const parsed = parseMetafyGuideUrl(raw.url);
  const url = parsed?.url ?? sanitizeMetafyArticleUrl(raw.url);
  if (!url) return undefined;

  const title =
    typeof raw.title === 'string' ? raw.title.trim().slice(0, METAFY_ARTICLE_TITLE_MAX_CHARS) : '';
  const coverUrl = typeof raw.coverUrl === 'string' ? raw.coverUrl.trim().slice(0, 500) : '';
  const priceCents =
    typeof raw.priceCents === 'number' && Number.isFinite(raw.priceCents)
      ? Math.max(0, Math.round(raw.priceCents))
      : undefined;
  const currency = typeof raw.currency === 'string' ? raw.currency.trim().slice(0, 8) : undefined;
  const subscriberOnly = raw.subscriberOnly === true;

  return {
    url,
    ...(title ? { title } : {}),
    ...(coverUrl ? { coverUrl } : {}),
    ...(priceCents !== undefined ? { priceCents } : {}),
    ...(currency ? { currency } : {}),
    ...(subscriberOnly ? { subscriberOnly } : {}),
  };
}

/** Price / access label. Null when we do not know. */
export function formatMetafyPriceLabel(article: Pick<DeckMetafyArticle, 'priceCents' | 'subscriberOnly'>): string | null {
  if (article.subscriberOnly) return 'Subscribers only';
  if (article.priceCents == null) return null;
  if (article.priceCents <= 0) return 'Free';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(article.priceCents / 100);
}
